// Dot Matrix Printer Utility
// Generates plain text receipts optimized for Epson LX-310

export class DotMatrixPrinter {
  constructor(width = 92) {
    this.width = width; // Total width: 3 + 60 + 5 + 12 + 12 = 92 chars for table
    this.ESC = "\x1B";
    this.lines = [];
  }

  // Center text
  center(text) {
    const padding = Math.floor((this.width - text.length) / 2);
    return " ".repeat(padding) + text;
  }

  // Add line
  addLine(text = "") {
    this.lines.push(text);
  }

  // Add centered line
  addCentered(text) {
    this.lines.push(this.center(text));
  }

  // Add separator line
  addSeparator(char = "=") {
    this.lines.push(char.repeat(this.width));
  }

  // Add dashed separator
  addDashedSeparator() {
    this.lines.push("-".repeat(this.width));
  }

  // Format line with label and value
  formatLine(label, value, separator = ":") {
    const maxLabelWidth = 20;
    const labelPart = label.padEnd(maxLabelWidth, " ");
    return `${labelPart}${separator} ${value}`;
  }

  // Format table row
  formatTableRow(columns, widths) {
    let row = "";
    columns.forEach((col, index) => {
      const width = widths[index];
      const text = String(col).substring(0, width);
      if (index === columns.length - 1) {
        // Right align last column (typically prices)
        row += text.padStart(width, " ");
      } else {
        row += text.padEnd(width, " ");
      }
      if (index < columns.length - 1) {
        row += " ";
      }
    });
    return row;
  }

  // Get all lines as text
  getText() {
    return this.lines.join("\n");
  }

  // Render an array of text lines as HTML (applies bigger font to title lines)
  renderLinesHtml(lines) {
    let html = "";
    lines.forEach((line) => {
      if (
        line.includes("*** SALES INVOICE ***") ||
        line.includes("*** JOB ORDER SLIP ***") ||
        line.includes("*** WARRANTY RECEIPT ***") ||
        line.includes("*** ACKNOWLEDGEMENT RECEIPT ***") ||
        line.includes("*** DOWN PAYMENT RECEIPT ***") ||
        line.includes("*** DELIVERY RECEIPT ***")
      ) {
        html += '<span class="big-title">' + line + "</span>\n";
      } else {
        html += line + "\n";
      }
    });
    return html;
  }

  // Render a single copy of the content as HTML
  renderCopyHtml() {
    return this.renderLinesHtml(this.getText().split("\n"));
  }

  // Render one copy whose content is split across two side-by-side columns,
  // halving its height. The lines are divided at their midpoint.
  renderSplitCopyHtml() {
    const lines = this.getText().split("\n");
    const mid = Math.ceil(lines.length / 2);
    const leftHtml = this.renderLinesHtml(lines.slice(0, mid));
    const rightHtml = this.renderLinesHtml(lines.slice(mid));
    return (
      '<div class="copy-split">' +
      '<div class="copy-col">' +
      leftHtml +
      "</div>" +
      '<div class="copy-col">' +
      rightHtml +
      "</div>" +
      "</div>"
    );
  }

  // Open the print window during the click, before any await. Browsers
  // block window.open once the handler has waited on a network call, which
  // is why Work History (it loads the problem first) never printed while
  // Job Order (printed inline) still did.
  static openForPrint() {
    const printWindow = window.open("", "_blank");
    if (!printWindow || printWindow.closed) {
      throw new Error("Pop-up blocked. Allow pop-ups for this site to print.");
    }
    return printWindow;
  }

  // Print to new window (for printing)
  // options.copies: number of copies that evenly divide the A4 page height
  //   (e.g. 2 => one copy in the top half, one in the bottom half) (default 1)
  // options.splitColumns: lay each copy out in two internal columns to halve
  //   its height (default false)
  // options.printWindow: a window already opened during the user click
  print(options = {}) {
    const copies = options.copies || 1;
    const split = options.splitColumns || false;
    const fillPage = copies > 1;
    // Shrink the font when fitting multiple copies on one page so each copy
    // fits within its page section without overflowing.
    const fontSize = fillPage ? (split ? 10 : 11) : 12;
    const lineHeight = fillPage ? 1.15 : 1.2;
    const printWindow = options.printWindow || window.open("", "_blank");
    if (!printWindow || printWindow.closed) {
      throw new Error("Pop-up blocked. Allow pop-ups for this site to print.");
    }
    // Explicit open so a window created before an await (already-loaded
    // about:blank) actually accepts the receipt HTML.
    printWindow.document.open();
    printWindow.document.write("<html><head><title>Print</title>");
    printWindow.document.write("<style>");
    printWindow.document.write("* { box-sizing: border-box; }");
    printWindow.document.write(
      'body { font-family: "Courier New", Courier, monospace; font-size: ' +
        fontSize +
        "px; line-height: " +
        lineHeight +
        "; margin: 0; padding: 0; }"
    );
    printWindow.document.write(
      ".big-title { font-size: " +
        fontSize +
        "px; font-weight: bold; display: block; text-align: center; white-space: normal; margin: 0; }"
    );
    printWindow.document.write(".copy { white-space: pre; }");
    printWindow.document.write(
      ".copy-split { display: flex; flex-direction: row; align-items: flex-start; gap: 16px; }"
    );
    printWindow.document.write(".copy-col { white-space: pre; }");

    if (fillPage) {
      // Divide the A4 page into equal-height sections, one per copy, so the
      // whole sheet is used (e.g. top half + bottom half for 2 copies).
      printWindow.document.write(
        ".page { width: 210mm; height: 297mm; display: flex; flex-direction: column; }"
      );
      printWindow.document.write(
        ".section { flex: 1 1 0; min-height: 0; padding: 8mm; }"
      );
      printWindow.document.write(
        ".section + .section { border-top: 1px dashed #999; }"
      );
      printWindow.document.write("@page { size: A4; margin: 0; }");
    } else {
      printWindow.document.write("@page { size: A4; margin: 0.25in; }");
    }

    printWindow.document.write("</style>");
    printWindow.document.write("</head><body>");

    const renderCopy = () =>
      split
        ? this.renderSplitCopyHtml()
        : '<div class="copy">' + this.renderCopyHtml() + "</div>";

    if (fillPage) {
      printWindow.document.write('<div class="page">');
      for (let i = 0; i < copies; i++) {
        printWindow.document.write(
          '<div class="section">' + renderCopy() + "</div>"
        );
      }
      printWindow.document.write("</div>");
    } else {
      printWindow.document.write(renderCopy());
    }

    printWindow.document.write("</body></html>");
    printWindow.document.close();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  }
}

// Wrap an invoice item's serial numbers into "S/N: ..." lines that fit the
// receipt's description column (JMB-242). Returns [] when there are none.
function serialLines(invoiceItem, maxWidth) {
  const serials = (invoiceItem.item_serials || []).map((s) => s.serial_number);
  if (!serials.length) return [];
  const lines = [];
  let current = "S/N:";
  serials.forEach((serial, idx) => {
    const piece = (idx === 0 ? " " : ", ") + serial;
    if ((current + piece).length <= maxWidth) {
      current += piece;
    } else {
      lines.push(current);
      current = "  " + serial;
    }
  });
  if (current.trim()) lines.push(current);
  return lines;
}

// Generate Invoice Receipt
export function generateInvoiceReceipt(invoice, displayPrice = true) {
  const printer = new DotMatrixPrinter();

  // Header
  printer.addCentered("JMB COMPUTER AND LAPTOP REPAIR SERVICES");
  printer.addCentered(
    "VAT REG | TIN:703-730-173 | Philgeps Platinum Registered"
  );
  printer.addCentered("Main Branch: P2, Magosilom, Cantilan, SDS");
  printer.addCentered("Tandag Branch: P Vila Bali, Mabua, Tandag City");
  printer.addSeparator();
  printer.addLine();

  // Title
  printer.addCentered("*** SALES INVOICE ***");
  printer.addLine();
  printer.addLine(`No. ${invoice.invoice_code}`);
  printer.addLine(`Date: ${invoice.invoice_date}`);
  printer.addDashedSeparator();

  // Customer Info
  if (invoice.customer) {
    printer.addLine(`Customer: ${invoice.customer.customer_name}`);
    printer.addLine(
      `Address: Purok ${invoice.customer.customer_purok}, Brgy.${invoice.customer.customer_barangay}, ${invoice.customer.customer_municipality}`
    );
    printer.addLine(`Contact: ${invoice.customer.customer_contact_no}`);
  } else {
    printer.addLine(`Customer: N/A`);
  }
  if (invoice.purpose) {
    printer.addLine(`Remarks: ${invoice.purpose}`);
  }
  printer.addDashedSeparator();

  // Items
  if (displayPrice) {
    printer.addLine(
      printer.formatTableRow(
        ["No", "Description", "Qty", "Price", "Total"],
        [3, 60, 5, 12, 12]
      )
    );
    printer.addDashedSeparator();

    let itemNo = 1;
    invoice.invoice_items.forEach((invoiceItem) => {
      // Skip tax fee item (id 650)
      if (invoiceItem.item && invoiceItem.item.id === 650) return;

      // Determine name and unit price based on type
      let itemName = "";
      let unitPrice = 0;
      if (invoiceItem.item) {
        itemName = invoiceItem.item.item_name;
        unitPrice = invoiceItem.item.selling_price;
      } else if (invoiceItem.service) {
        itemName = invoiceItem.service.service_name + " (Service)";
        unitPrice = invoiceItem.service.service_amount;
      } else {
        return; // Skip if neither item nor service
      }

      const maxDescWidth = 60;

      // Break description into multiple lines if needed
      if (itemName.length <= maxDescWidth) {
        // Fits in one line
        printer.addLine(
          printer.formatTableRow(
            [
              itemNo++,
              itemName,
              invoiceItem.quantity,
              unitPrice.toFixed(2),
              invoiceItem.amount.toFixed(2),
            ],
            [3, 60, 5, 12, 12]
          )
        );
      } else {
        // Split into multiple lines
        const words = itemName.split(" ");
        let lines = [];
        let currentLine = "";

        words.forEach((word) => {
          if ((currentLine + " " + word).trim().length <= maxDescWidth) {
            currentLine = (currentLine + " " + word).trim();
          } else {
            if (currentLine) lines.push(currentLine);
            currentLine = word;
          }
        });
        if (currentLine) lines.push(currentLine);

        // Print first line with item number and prices
        printer.addLine(
          printer.formatTableRow(
            [
              itemNo++,
              lines[0],
              invoiceItem.quantity,
              unitPrice.toFixed(2),
              invoiceItem.amount.toFixed(2),
            ],
            [3, 60, 5, 12, 12]
          )
        );

        // Print continuation lines (indented, no item number)
        for (let i = 1; i < lines.length; i++) {
          printer.addLine(
            printer.formatTableRow(
              ["", lines[i], "", "", ""],
              [3, 60, 5, 12, 12]
            )
          );
        }
      }

      // Serial numbers of the sold units (JMB-242)
      serialLines(invoiceItem, 60).forEach((line) => {
        printer.addLine(
          printer.formatTableRow(["", line, "", "", ""], [3, 60, 5, 12, 12])
        );
      });
    });

    printer.addDashedSeparator();
    printer.addLine(printer.formatLine("Subtotal", invoice.amount.toFixed(2)));
    if (invoice.discount > 0) {
      printer.addLine(
        printer.formatLine("Discount", `(${invoice.discount.toFixed(2)})`)
      );
    }
    printer.addLine(
      printer.formatLine("TOTAL", `PHP ${invoice.total_amount.toFixed(2)}`)
    );
    printer.addLine(
      printer.formatLine(
        "Amount Paid",
        (invoice.total_amount - invoice.balance).toFixed(2)
      )
    );
    printer.addLine(printer.formatLine("Balance", invoice.balance.toFixed(2)));
    printer.addLine(printer.formatLine("Status", invoice.invoice_status));
  } else {
    printer.addLine(
      printer.formatTableRow(["No", "Description", "Quantity"], [3, 50, 10])
    );
    printer.addDashedSeparator();

    let itemNo = 1;
    invoice.invoice_items.forEach((invoiceItem) => {
      // Skip tax fee item (id 650)
      if (invoiceItem.item && invoiceItem.item.id === 650) return;

      let itemName = "";
      if (invoiceItem.item) {
        itemName = invoiceItem.item.item_name;
      } else if (invoiceItem.service) {
        itemName = invoiceItem.service.service_name + " (Service)";
      } else {
        return;
      }

      const desc = itemName.substring(0, 50);
      printer.addLine(
        printer.formatTableRow(
          [itemNo++, desc, invoiceItem.quantity],
          [3, 50, 10]
        )
      );

      // Serial numbers of the sold units (JMB-242)
      serialLines(invoiceItem, 50).forEach((line) => {
        printer.addLine(printer.formatTableRow(["", line, ""], [3, 50, 10]));
      });
    });
  }

  printer.addDashedSeparator();
  printer.addLine();
  printer.addLine("Payment Received By: _____________________");
  printer.addLine();

  printer.addLine(`Prepared by: ${invoice.prepared_by || "Admin"}`);
  printer.addLine(`Printed: ${new Date().toLocaleString()}`);
  printer.addLine();
  printer.addCentered("Thank you for your business!");
  return printer;
}

// Generate Acknowledgement Receipt (acknowledges receipt of a payment)
export function generateAcknowledgementReceipt(invoice, payment, width = 92) {
  const printer = new DotMatrixPrinter(width);

  // Header
  printer.addCentered("JMB COMPUTER AND LAPTOP REPAIR SERVICES");
  printer.addCentered(
    "VAT REG | TIN:703-730-173 | Philgeps Platinum Registered"
  );
  printer.addCentered("Main Branch: P2, Magosilom, Cantilan, SDS");
  printer.addCentered("Tandag Branch: P Vila Bali, Mabua, Tandag City");
  printer.addSeparator();
  printer.addLine();

  // Title
  printer.addCentered("*** ACKNOWLEDGEMENT RECEIPT ***");
  printer.addLine();
  printer.addLine(`AR No. ${payment.id}`);
  printer.addLine(`Invoice No. ${invoice.invoice_code}`);
  printer.addLine(
    `Date: ${payment.payment_date || new Date().toLocaleDateString()}`
  );
  printer.addDashedSeparator();

  // Customer Info
  if (invoice.customer) {
    printer.addLine(`Received from: ${invoice.customer.customer_name}`);
    printer.addLine(
      `Address: Purok ${invoice.customer.customer_purok}, Brgy.${invoice.customer.customer_barangay}, ${invoice.customer.customer_municipality}`
    );
    printer.addLine(`Contact: ${invoice.customer.customer_contact_no}`);
  } else {
    printer.addLine(`Received from: N/A`);
  }
  printer.addDashedSeparator();
  printer.addLine();

  // Acknowledgement statement
  const amountPaid = Number(payment.amount_paid || 0);
  printer.addLine(
    `This acknowledges receipt of the sum of PHP ${amountPaid.toFixed(2)}`
  );
  printer.addLine(`as payment for Invoice No. ${invoice.invoice_code}.`);
  printer.addLine();

  // Payment details
  printer.addLine(
    printer.formatLine("Amount Paid", `PHP ${amountPaid.toFixed(2)}`)
  );
  printer.addLine(
    printer.formatLine(
      "Payment Method",
      payment.chart_of_account ? payment.chart_of_account.account_title : "N/A"
    )
  );
  printer.addLine(
    printer.formatLine("Payment Date", payment.payment_date || "N/A")
  );
  printer.addLine(printer.formatLine("OR Number", payment.or_number || "N/A"));
  printer.addLine(printer.formatLine("Tax Code", payment.tax_code || "N/A"));
  printer.addDashedSeparator();

  // Invoice summary
  printer.addLine(
    printer.formatLine(
      "Invoice Total",
      `PHP ${Number(invoice.total_amount || 0).toFixed(2)}`
    )
  );
  printer.addLine(
    printer.formatLine(
      "Remaining Balance",
      `PHP ${Number(invoice.balance || 0).toFixed(2)}`
    )
  );
  printer.addLine(
    printer.formatLine("Status", invoice.invoice_status || "N/A")
  );
  printer.addDashedSeparator();
  printer.addLine();

  printer.addLine(
    `Received by: ${
      payment.user ? payment.user.full_name : "_____________________"
    }`
  );
  printer.addLine();
  printer.addLine("Customer Signature: _____________________");
  printer.addLine();
  printer.addLine(`Printed: ${new Date().toLocaleString()}`);
  printer.addLine();
  printer.addCentered("Thank you for your payment!");
  return printer;
}

// Generate Down Payment AR Slip (acknowledges receipt of an advanced downpayment)
export function generateDownpaymentReceipt(
  downpayment,
  user = null,
  width = 92
) {
  const printer = new DotMatrixPrinter(width);

  // Header
  printer.addCentered("JMB COMPUTER AND LAPTOP REPAIR SERVICES");
  printer.addCentered(
    "VAT REG | TIN:703-730-173 | Philgeps Platinum Registered"
  );
  printer.addCentered("Main Branch: P2, Magosilom, Cantilan, SDS");
  printer.addCentered("Tandag Branch: P Vila Bali, Mabua, Tandag City");
  printer.addSeparator();
  printer.addLine();

  // Title
  printer.addCentered("*** DOWN PAYMENT RECEIPT ***");
  printer.addLine();
  printer.addLine(
    `DP Ref No. ${downpayment.reference_number || downpayment.id}`
  );
  printer.addLine(
    `Date: ${downpayment.payment_date || new Date().toLocaleDateString()}`
  );
  printer.addDashedSeparator();

  // Customer Info
  if (downpayment.customer) {
    printer.addLine(`Received from: ${downpayment.customer.customer_name}`);
    printer.addLine(
      `Address: Purok ${downpayment.customer.customer_purok}, Brgy.${downpayment.customer.customer_barangay}, ${downpayment.customer.customer_municipality}`
    );
    printer.addLine(`Contact: ${downpayment.customer.customer_contact_no}`);
  } else {
    printer.addLine(`Received from: N/A`);
  }
  printer.addDashedSeparator();
  printer.addLine();

  // Acknowledgement statement
  const amountPaid = Number(downpayment.amount_paid || 0);
  printer.addLine(
    `This acknowledges receipt of the sum of PHP ${amountPaid.toFixed(2)}`
  );
  printer.addLine(`as an advanced down payment.`);
  printer.addLine();

  // Down payment details
  printer.addLine(
    printer.formatLine("Amount Paid", `PHP ${amountPaid.toFixed(2)}`)
  );
  printer.addLine(
    printer.formatLine(
      "Payment Method",
      downpayment.chart_of_account
        ? downpayment.chart_of_account.account_title
        : "N/A"
    )
  );
  printer.addLine(
    printer.formatLine("Payment Date", downpayment.payment_date || "N/A")
  );
  printer.addLine(
    printer.formatLine("Remarks", downpayment.description || "N/A")
  );
  printer.addDashedSeparator();
  printer.addLine();

  printer.addLine(
    `Received by: ${
      user && user.full_name
        ? user.full_name
        : downpayment.posted_by || "_____________________"
    }`
  );
  printer.addLine();
  printer.addLine("Customer Signature: _____________________");
  printer.addLine();
  printer.addLine(`Printed: ${new Date().toLocaleString()}`);
  printer.addLine();
  printer.addCentered("Keep this slip. Present it to apply your down payment.");
  return printer;
}

// Generate Job Order Receipt
export function generateJobOrderReceipt(unit) {
  const printer = new DotMatrixPrinter();

  // Header
  printer.addCentered("JMB COMPUTER AND LAPTOP REPAIR SERVICES");
  printer.addCentered(
    "VAT REG | TIN:703-730-173 | Philgeps Platinum Registered"
  );
  printer.addCentered("Main Branch: P2, Magosilom, Cantilan, SDS");
  printer.addCentered("Tandag Branch: P Vila Bali, Mabua, Tandag City");
  printer.addSeparator();
  printer.addLine();

  // Title
  printer.addCentered("*** JOB ORDER SLIP ***");
  printer.addLine();
  printer.addLine(`No. JMBCJOS-${unit.id}-23`);
  printer.addLine(`Date: ${new Date().toLocaleDateString()}`);
  printer.addDashedSeparator();

  // Customer and Unit Info in Two Columns
  const leftColWidth = 46;
  const rightColWidth = 46;

  // Prepare customer lines
  const customerLines = [
    "PREPARED FOR:",
    unit.customer.customer_name.substring(0, leftColWidth),
    `P-${unit.customer.customer_purok}, ${unit.customer.customer_barangay}`.substring(
      0,
      leftColWidth
    ),
    `${unit.customer.customer_municipality}, SDS`.substring(0, leftColWidth),
    unit.customer.customer_contact_no.substring(0, leftColWidth),
  ];

  // Prepare unit info lines
  const unitLines = [
    "UNIT INFORMATION:",
    `Type: ${unit.unit_type}`.substring(0, rightColWidth),
    `Brand: ${unit.unit_brand || "N/A"}`.substring(0, rightColWidth),
    `Model: ${unit.unit_model || "N/A"}`.substring(0, rightColWidth),
    `Serial: ${unit.serial_no || "N/A"}`.substring(0, rightColWidth),
  ];

  // Print side by side
  const maxLines = Math.max(customerLines.length, unitLines.length);
  for (let i = 0; i < maxLines; i++) {
    const leftText = (customerLines[i] || "").padEnd(leftColWidth, " ");
    const rightText = unitLines[i] || "";
    printer.addLine(leftText + rightText);
  }
  printer.addLine();

  // Problems
  printer.addLine("INITIAL PROBLEM/S:");
  printer.addDashedSeparator();
  if (unit.problems && unit.problems.length > 0) {
    unit.problems.forEach((problem) => {
      const description = problem.problem_description || "";
      // Wrap text at 78 characters
      const words = description.split(" ");
      let line = "";
      words.forEach((word) => {
        if (line.length + word.length + 1 <= 78) {
          line += (line ? " " : "") + word;
        } else {
          printer.addLine(line);
          line = word;
        }
      });
      if (line) printer.addLine(line);
    });
  }
  printer.addDashedSeparator();
  printer.addLine();

  // Terms and Conditions (Complete)
  printer.addLine("TERMS AND CONDITIONS:");
  printer.addLine();
  printer.addLine(
    "1. Liability: Our business is not liable for any damages, external or"
  );
  printer.addLine(
    "   internal, that may occur to your device after it has been removed"
  );
  printer.addLine(
    "   from our shop, especially if the damage is not related to the"
  );
  printer.addLine("   service we rendered.");
  printer.addLine();
  printer.addLine(
    "2. Warranty: After the warranty period has expired, we are not obliged"
  );
  printer.addLine(
    "   to repair or replace items for free. Additional charges may apply."
  );
  printer.addLine();
  printer.addLine(
    "3. Device Pick-up & Storage: You'll be notified of pick-up via text,"
  );
  printer.addLine(
    "   email or chat after repair. If not picked up within 15 days, daily"
  );
  printer.addLine(
    "   storage fee of 15 pesos applies. After 3 months, device becomes"
  );
  printer.addLine("   our property.");
  printer.addLine();
  printer.addLine(
    "4. Diagnostic Fee: A fee of 450 pesos will be initially charged for"
  );
  printer.addLine(
    "   Laptop/Desktop, and Printer diagnosis. Said amount will be deducted"
  );
  printer.addLine("   to the total amount and other charges due upon pick up.");
  printer.addLine("   NOTE: This is non-refundable.");
  printer.addLine();
  printer.addLine(
    "5. Mode of Payment: We accept cash and online payments. Downpayments,"
  );
  printer.addLine("   if required, must be paid before processing of order.");
  printer.addLine();
  printer.addLine(
    "6. Data Backup: Backup all important data before repair. We are not"
  );
  printer.addLine("   responsible for data loss.");
  printer.addLine();
  printer.addLine(
    "7. Changes: These terms and conditions are subject to change at any"
  );
  printer.addLine("   time without notice.");
  printer.addLine();
  printer.addLine(
    "8. Acceptance: By leaving your device for repair, you are accepting"
  );
  printer.addLine("   these terms and conditions.");
  printer.addLine();
  printer.addLine("Signed: _____________________");
  printer.addLine();
  printer.addLine("Thank you for doing business with us!");

  return printer;
}

function asText(value) {
  if (value === null || value === undefined) return "";
  return String(value);
}

// actions_performed / other_remarks are stored as a JSON string, an array,
// or sometimes null. Anything else becomes an empty list so printing continues.
function asList(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
}

// The print endpoint is supposed to return the problem with customer and unit.
// When it doesn't (bare model, HTML error page, or a failed request), fill
// those in from the unit already on screen so the slip can still be printed.
export function buildWorkHistoryProblem(apiData, localProblem, unit) {
  const api =
    apiData && typeof apiData === "object" && !Array.isArray(apiData)
      ? apiData
      : null;
  const local =
    localProblem && typeof localProblem === "object" ? localProblem : {};
  const parentUnit = unit && typeof unit === "object" ? unit : {};
  const apiLooksLikeProblem =
    api &&
    (api.id != null ||
      api.problem_description != null ||
      api.customer ||
      api.unit);
  const base = apiLooksLikeProblem ? { ...local, ...api } : { ...local };
  const resolvedUnit = base.unit || parentUnit;
  const resolvedCustomer =
    base.customer || parentUnit.customer || resolvedUnit.customer || {};
  return {
    ...base,
    unit: resolvedUnit || {},
    customer: resolvedCustomer,
  };
}

// Generate Work History/QA Checklist Receipt
export function generateWorkHistoryReceipt(problem) {
  const printer = new DotMatrixPrinter();
  problem = problem || {};
  const customer = problem.customer || {};
  const unit = problem.unit || {};

  // Header
  printer.addCentered("JMB COMPUTER AND LAPTOP REPAIR SERVICES");
  printer.addCentered("VAT REG | TIN:703-730-173");
  printer.addSeparator();
  printer.addLine();

  // Title
  printer.addCentered("*** WORK SUMMARY AND QA CHECKLIST ***");
  printer.addLine();

  // Customer and Unit Info in Two Columns
  const leftColWidth = 46;
  const rightColWidth = 46;

  // Prepare customer lines
  const customerLines = [
    "PREPARED FOR:",
    asText(customer.customer_name).substring(0, leftColWidth),
    `P-${asText(customer.customer_purok)}, Brgy.${asText(
      customer.customer_barangay
    )}`.substring(0, leftColWidth),
    `${asText(customer.customer_municipality)}, SDS`.substring(0, leftColWidth),
    asText(customer.customer_contact_no).substring(0, leftColWidth),
  ];

  // Prepare unit info lines
  const unitLines = [
    "UNIT INFORMATION:",
    `Type: ${asText(unit.unit_type) || "N/A"}`.substring(0, rightColWidth),
    `Brand: ${asText(unit.unit_brand) || "N/A"}`.substring(0, rightColWidth),
    `Model: ${asText(unit.unit_model) || "N/A"}`.substring(0, rightColWidth),
    `S/N: ${asText(unit.serial_no) || "N/A"}`.substring(0, rightColWidth),
  ];

  // Print side by side
  const maxLines = Math.max(customerLines.length, unitLines.length);
  for (let i = 0; i < maxLines; i++) {
    const leftText = (customerLines[i] || "").padEnd(leftColWidth, " ");
    const rightText = unitLines[i] || "";
    printer.addLine(leftText + rightText);
  }
  printer.addLine();

  // Initial Problem and Status Info
  printer.addLine(
    `Initial Problem: ${problem.problem_description || "N/A"}`.substring(0, 92)
  );
  printer.addLine();
  printer.addLine(`Status: ${problem.status || "N/A"}`);
  printer.addLine(`Technician: ${problem.technician || "N/A"}`);
  printer.addLine(`Repair Date: ${problem.repair_initialized || "N/A"}`);
  printer.addDashedSeparator();

  // Quality Assurance Checklist
  printer.addLine();
  printer.addLine(
    `QUALITY ASSURANCE CHECKLIST (${asText(unit.unit_type) || "N/A"})`
  );
  printer.addLine();
  printer.addLine(
    printer.formatTableRow(
      ["Category", "Description", "OK", "Repair", "Remarks"],
      [15, 35, 5, 7, 15]
    )
  );
  printer.addDashedSeparator();

  // QA Items based on unit type
  let qaItems = [];

  if (unit.unit_type === "Laptop") {
    qaItems = [
      ["System Boot", "Does the unit boots up?"],
      ["Display Test", "Does the unit display?"],
      ["Battery", "Battery Check"],
      ["USB Ports", "USB Ports functions well?"],
      ["Trackpad", "Trackpad functions well?"],
      ["Keyboard", "All keys function well?"],
      ["Camera", "Camera functions well?"],
      ["Speakers", "Speakers function well?"],
      ["Microphone", "Microphone functions well?"],
      ["Audio Jack", "External speaker works?"],
      ["Wifi", "Wifi functions well?"],
      ["Performance", "Unit performs well?"],
      ["Loose Parts", "Unit has loose parts?"],
      ["Charging", "Detects charger well?"],
    ];
  } else if (unit.unit_type === "Printer") {
    qaItems = [
      ["Print-out", "Prints CYMK colors well?"],
      ["Scan", "Able to scan documents?"],
      ["Print Function", "Prints out normally?"],
      ["Power", "Printer turns on?"],
    ];
  } else if (unit.unit_type === "Desktop PC") {
    qaItems = [
      ["System Boot", "Does the unit boots up?"],
      ["Display Test", "Does the unit display?"],
      ["USB Ports", "USB Ports functions well?"],
      ["Audio Jack", "External speaker works?"],
      ["Performance", "Unit performs well?"],
      ["Loose Parts", "Unit has loose parts?"],
    ];
  }

  qaItems.forEach((item) => {
    const category = item[0].substring(0, 15);
    const description = item[1].substring(0, 35);
    printer.addLine(
      printer.formatTableRow(
        [category, description, "[ ]", "[ ]", ""],
        [15, 35, 5, 7, 15]
      )
    );
  });

  printer.addDashedSeparator();
  printer.addLine();

  // Work History - Actions Performed
  printer.addLine("ACTIONS PERFORMED:");
  printer.addLine();
  // Header row - left align both columns
  const actionsHeader = "No.".padEnd(5, " ") + " " + "Action".padEnd(82, " ");
  printer.addLine(actionsHeader);
  printer.addDashedSeparator();

  const actions = asList(problem.actions_performed);

  // Display actions - left align both columns
  if (actions.length > 0) {
    actions.forEach((action, index) => {
      const actionText = (action || "").toString().substring(0, 82);
      const row =
        String(index + 1).padEnd(5, " ") + " " + actionText.padEnd(82, " ");
      printer.addLine(row);
    });
  } else {
    // If no actions, show empty rows for manual entry
    for (let i = 1; i <= 3; i++) {
      const row = String(i).padEnd(5, " ") + " " + "".padEnd(82, " ");
      printer.addLine(row);
    }
  }

  printer.addDashedSeparator();
  printer.addLine();

  // Work History - Other Remarks
  printer.addLine("OTHER REMARKS:");
  printer.addLine();
  // Header row - left align both columns
  const remarksHeader = "No.".padEnd(5, " ") + " " + "Remarks".padEnd(82, " ");
  printer.addLine(remarksHeader);
  printer.addDashedSeparator();

  const otherRemarks = asList(problem.other_remarks);

  // Display remarks - left align both columns
  if (otherRemarks.length > 0) {
    otherRemarks.forEach((remark, index) => {
      const remarkText = (remark || "").toString().substring(0, 82);
      const row =
        String(index + 1).padEnd(5, " ") + " " + remarkText.padEnd(82, " ");
      printer.addLine(row);
    });
  } else {
    // If no remarks, show empty rows for manual entry
    for (let i = 1; i <= 3; i++) {
      const row = String(i).padEnd(5, " ") + " " + "".padEnd(82, " ");
      printer.addLine(row);
    }
  }

  printer.addDashedSeparator();
  printer.addLine();

  // Picked Up Information
  printer.addLine("PICKUP INFORMATION:");
  printer.addLine();
  printer.addLine(
    `Picked up date: ${problem.picked_up_at || "_____________________________"}`
  );
  printer.addLine(
    `Picked up by: ${problem.picked_up_by || "_____________________________"}`
  );
  printer.addLine();
  printer.addLine("Customer Signature: _____________________");
  printer.addLine();
  printer.addLine(`Date: ${new Date().toLocaleDateString()}`);

  return printer;
}

// Warranty terms by type
// Format two arrays of lines side-by-side in two columns
function formatTwoColumns(leftLines, rightLines, totalWidth) {
  const colWidth = Math.floor((totalWidth - 4) / 2); // 4-char gutter
  const gutter = " ".repeat(4);
  const maxRows = Math.max(leftLines.length, rightLines.length);
  const result = [];
  for (let i = 0; i < maxRows; i++) {
    const left = (leftLines[i] || "").substring(0, colWidth).padEnd(colWidth);
    const right = (rightLines[i] || "").substring(0, colWidth);
    result.push(left + gutter + right);
  }
  return result;
}

function centerInColumn(text, colWidth) {
  const padding = Math.floor((colWidth - text.length) / 2);
  return " ".repeat(Math.max(0, padding)) + text;
}

function getDesktopWarrantyLines(colWidth) {
  return [
    centerInColumn("WARRANTY COVERAGE:", colWidth),
    centerInColumn("DESKTOP PC SETS", colWidth),
    "",
    "Major Parts (6 Months):",
    "  Includes Motherboard, Processors",
    "  (CPU), RAM, and Graphics Cards",
    "  (GPU).",
    "",
    "Minor Parts (3 Months):",
    "  Includes Power Supply (PSU),",
    "  Storage Drives (SSD/HDD), Fans,",
    "  and Casing.",
    "",
    "Service (1 Year):",
    "  Labor for repairs and diagnostics",
    "  is free for twelve months.",
  ];
}

function getLaptopWarrantyLines(colWidth) {
  return [
    centerInColumn("WARRANTY COVERAGE:", colWidth),
    centerInColumn("LAPTOPS", colWidth),
    "",
    "7-Day Outright Replacement:",
    "  If a factory defect is found",
    "  within the first 7 days of",
    "  purchase, the unit will be",
    "  replaced with a new one (subject",
    "  to stock availability and",
    "  physical condition).",
    "",
    "1-Year Warehouse Warranty:",
    "  After 7 days, repairs are covered",
    "  under a warehouse warranty. The",
    "  unit must be brought or shipped",
    "  to our service center for repair.",
  ];
}

function getCctvWarrantyLines(colWidth) {
  return [
    centerInColumn("WARRANTY COVERAGE:", colWidth),
    centerInColumn("CCTV SYSTEMS", colWidth),
    "",
    "1-Year Parts Replacement:",
    "  Covers DVR/NVR and Camera units.",
    "",
    "1-Month Service:",
    "  Free on-site or in-shop labor for",
    "  configuration or hardware",
    "  adjustment for the first 30 days.",
  ];
}

function getRepairsWarrantyLines(colWidth) {
  return [
    centerInColumn("WARRANTY COVERAGE:", colWidth),
    centerInColumn("REPAIRS & REPLACEMENTS", colWidth),
    "",
    "Warranty applies only to the",
    "specific part replaced. It does",
    "not extend to other existing",
    "components of the device.",
  ];
}

function getPrinterWarrantyLines(colWidth) {
  return [
    centerInColumn("WARRANTY COVERAGE:", colWidth),
    centerInColumn("PRINTERS", colWidth),
    "",
    "3-Month JMB Service Warranty:",
    "  Free labor for repairs and",
    "  diagnostics for the first three",
    "  months from date of purchase.",
    "",
    "2-Year Brand Service Warranty:",
    "  Covered under the manufacturer's",
    "  warranty. Unit must be brought to",
    "  an authorized brand service",
    "  center.",
  ];
}

function getRegularWarrantyLines(colWidth) {
  return [
    centerInColumn("WARRANTY COVERAGE", colWidth),
    "",
    "Each item is covered by its own",
    "individual warranty as indicated",
    "above. Warranty period begins from",
    "the date of purchase shown on this",
    "receipt.",
  ];
}

function getGeneralTermsLines(colWidth) {
  return [
    centerInColumn("GENERAL TERMS & CONDITIONS", colWidth),
    "",
    "Proof of Purchase:",
    "  A valid sales invoice or official",
    "  receipt is required for all",
    "  warranty claims.",
    "",
    "Voiding the Warranty:",
    "  The warranty is void if:",
    "  - Warranty seal is broken/removed",
    "  - Physical damage, liquid spills,",
    "    or burns (short circuits)",
    '  - "Acts of God" (lightning,',
    "    floods, power surges)",
    "  - Unauthorized repair attempts",
    "",
    "Software & Data:",
    "  We are not responsible for data",
    "  loss. Back up before surrendering",
    "  units. Software/virus issues are",
    "  not covered by hardware warranty.",
    "",
    "Turnaround Time:",
    "  Warranty service may take 7-21",
    "  business days depending on part",
    "  availability.",
    "",
    "How to Claim:",
    "  Bring your item and receipt to",
    "  our service center. For Laptop",
    "  'Warehouse' claims, ensure the",
    "  device is securely packed.",
    "",
    "Note: Replacement parts may be new",
    "or refurbished to factory specs, at",
    "the discretion of the service",
    "center.",
  ];
}

function addWarrantyAndTermsTwoColumn(printer, warrantyType) {
  const colWidth = Math.floor((printer.width - 4) / 2);
  let warrantyLines;
  switch (warrantyType) {
    case "desktop":
      warrantyLines = getDesktopWarrantyLines(colWidth);
      break;
    case "laptop":
      warrantyLines = getLaptopWarrantyLines(colWidth);
      break;
    case "cctv":
      warrantyLines = getCctvWarrantyLines(colWidth);
      break;
    case "repairs":
      warrantyLines = getRepairsWarrantyLines(colWidth);
      break;
    case "printer":
      warrantyLines = getPrinterWarrantyLines(colWidth);
      break;
    case "regular":
    default:
      warrantyLines = getRegularWarrantyLines(colWidth);
      break;
  }
  const termsLines = getGeneralTermsLines(colWidth);
  const twoColLines = formatTwoColumns(
    warrantyLines,
    termsLines,
    printer.width
  );
  twoColLines.forEach((line) => printer.addLine(line));
}

// Generate Delivery Receipt (typewritten style, with serials per item)
export function generateDeliveryReceipt(invoice) {
  const printer = new DotMatrixPrinter();

  // Header
  printer.addCentered("JMB COMPUTER AND LAPTOP REPAIR SERVICES");
  printer.addCentered(
    "VAT REG | TIN:703-730-173 | Philgeps Platinum Registered"
  );
  printer.addCentered("Main Branch: P2, Magosilom, Cantilan, SDS");
  printer.addCentered("Tandag Branch: P Vila Bali, Mabua, Tandag City");
  printer.addSeparator();
  printer.addLine();

  // Title
  printer.addCentered("*** DELIVERY RECEIPT ***");
  printer.addLine();
  printer.addLine(`DR No. ${invoice.invoice_code}`);
  printer.addLine(`Invoice Ref #: ${invoice.invoice_code}`);
  printer.addLine(`Invoice Date: ${invoice.invoice_date}`);
  printer.addLine(`Delivery Date: ${new Date().toLocaleDateString()}`);
  printer.addDashedSeparator();

  // Customer Info
  if (invoice.customer) {
    printer.addLine(`Delivered To: ${invoice.customer.customer_name}`);
    printer.addLine(
      `Address: Purok ${invoice.customer.customer_purok}, Brgy.${invoice.customer.customer_barangay}, ${invoice.customer.customer_municipality}`
    );
    printer.addLine(`Contact: ${invoice.customer.customer_contact_no}`);
  } else {
    printer.addLine(`Delivered To: N/A`);
  }
  if (invoice.purpose) {
    printer.addLine(`Purpose/Remarks: ${invoice.purpose}`);
  }
  printer.addDashedSeparator();

  // Items (quantities only — a DR carries no prices)
  printer.addLine(
    printer.formatTableRow(["No", "Description", "Qty", "Unit"], [3, 70, 6, 8])
  );
  printer.addDashedSeparator();

  const maxDescWidth = 70;
  let itemNo = 1;
  invoice.invoice_items.forEach((invoiceItem) => {
    // Skip tax fee item (id 650)
    if (invoiceItem.item && invoiceItem.item.id === 650) return;

    let itemName = "";
    let unit = "pcs";
    if (invoiceItem.item) {
      itemName = invoiceItem.item.item_name;
    } else if (invoiceItem.service) {
      itemName = invoiceItem.service.service_name + " (Service)";
      unit = "lot";
    } else {
      return;
    }

    // Break description into multiple lines if needed
    const words = itemName.split(" ");
    let lines = [];
    let currentLine = "";
    words.forEach((word) => {
      if ((currentLine + " " + word).trim().length <= maxDescWidth) {
        currentLine = (currentLine + " " + word).trim();
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    });
    if (currentLine) lines.push(currentLine);

    printer.addLine(
      printer.formatTableRow(
        [itemNo++, lines[0], invoiceItem.quantity, unit],
        [3, 70, 6, 8]
      )
    );
    for (let i = 1; i < lines.length; i++) {
      printer.addLine(
        printer.formatTableRow(["", lines[i], "", ""], [3, 70, 6, 8])
      );
    }

    // Serial numbers of the delivered units (JMB-242)
    serialLines(invoiceItem, maxDescWidth).forEach((line) => {
      printer.addLine(
        printer.formatTableRow(["", line, "", ""], [3, 70, 6, 8])
      );
    });
  });

  printer.addDashedSeparator();
  printer.addLine();

  // Acknowledgment statement
  printer.addLine(
    "This is to acknowledge that the above-listed items have been delivered"
  );
  printer.addLine(
    "and received in CORRECT and COMPLETE condition and in good working order."
  );
  printer.addLine(
    "Any discrepancy, shortage, or damage must be reported to JMB Computers"
  );
  printer.addLine("within twenty-four (24) hours from receipt.");
  printer.addLine();
  printer.addDashedSeparator();
  printer.addLine();

  // Signature blocks (two columns)
  const sigLines = formatTwoColumns(
    [
      "Delivered / Released by:",
      "",
      "",
      "_______________________________",
      "Signature over Printed Name",
      "",
      "Date: _________________________",
    ],
    [
      "Received by (Receiving Staff):",
      "",
      "",
      "_______________________________",
      "Signature over Printed Name",
      "",
      "Designation: __________________",
      "Date: _________________________",
    ],
    printer.width
  );
  sigLines.forEach((line) => printer.addLine(line));

  printer.addLine();
  printer.addLine(`Printed: ${new Date().toLocaleString()}`);

  return printer;
}

// Generate Warranty Receipt
export function generateWarrantyReceipt(
  invoice,
  printType = "mixed",
  remarks = "",
  warrantyType = "regular"
) {
  const printer = new DotMatrixPrinter();
  // Header
  printer.addCentered("JMB COMPUTER AND LAPTOP REPAIR SERVICES");
  printer.addCentered(
    "VAT REG | TIN:703-730-173 | Philgeps Platinum Registered"
  );
  printer.addCentered("Main Branch: P2, Magosilom, Cantilan, SDS");
  printer.addCentered("Tandag Branch: P Vila Bali, Mabua, Tandag City");
  printer.addSeparator();
  printer.addLine();

  // Title
  printer.addCentered("*** WARRANTY CERTIFICATE ***");
  printer.addLine();
  printer.addLine(`Invoice #: ${invoice.invoice_code}`);
  printer.addLine(`Date: ${invoice.invoice_date}`);
  printer.addDashedSeparator();

  // Customer
  if (invoice.customer) {
    printer.addLine(`Name: ${invoice.customer.customer_name}`);
    printer.addLine(`Contact: ${invoice.customer.customer_contact_no}`);
    printer.addLine(
      `Address: Purok ${invoice.customer.customer_purok}, ${invoice.customer.customer_barangay}, ${invoice.customer.customer_municipality}`
    );
  } else {
    printer.addLine(`Name: N/A`);
  }
  printer.addDashedSeparator();

  // Items
  if (printType === "mixed") {
    printer.addLine(
      printer.formatTableRow(
        ["ID", "Description", "Batch", "Warranty"],
        [5, 35, 20, 12]
      )
    );
  } else {
    printer.addLine(
      printer.formatTableRow(
        ["ID", "Description", "Batch", "Qty"],
        [5, 35, 20, 8]
      )
    );
  }
  printer.addDashedSeparator();

  invoice.invoice_items.forEach((invoiceItem) => {
    // Skip tax fee item (id 650)
    if (invoiceItem.item && invoiceItem.item.id === 650) return;

    let desc = "";
    let batch = "N/A";
    let warranty = "N/A";
    let itemId = "";

    if (invoiceItem.item) {
      desc = invoiceItem.item.item_name.substring(0, 35);
      batch =
        invoiceItem.item.import_batch?.batch_description?.substring(0, 20) ||
        "N/A";
      warranty = invoiceItem.item.warranty
        ? `${invoiceItem.item.warranty.warranty_count}-${invoiceItem.item.warranty.warranty_duration}`
        : "N/A";
      itemId = invoiceItem.item.id;
    } else if (invoiceItem.service) {
      desc = (invoiceItem.service.service_name + " (Svc)").substring(0, 35);
      warranty = invoiceItem.service.warranty
        ? `${invoiceItem.service.warranty.warranty_count}-${invoiceItem.service.warranty.warranty_duration}`
        : "N/A";
      itemId = "S" + invoiceItem.service.id;
    } else {
      return;
    }

    if (printType === "mixed") {
      printer.addLine(
        printer.formatTableRow([itemId, desc, batch, warranty], [5, 35, 20, 12])
      );
    } else {
      printer.addLine(
        printer.formatTableRow(
          [itemId, desc, batch, invoiceItem.quantity],
          [5, 35, 20, 8]
        )
      );
    }

    // Serial numbers of the covered units (JMB-244)
    serialLines(invoiceItem, 35).forEach((line) => {
      if (printType === "mixed") {
        printer.addLine(
          printer.formatTableRow(["", line, "", ""], [5, 35, 20, 12])
        );
      } else {
        printer.addLine(
          printer.formatTableRow(["", line, "", ""], [5, 35, 20, 8])
        );
      }
    });
  });

  if (remarks) {
    printer.addDashedSeparator();
    printer.addLine(`Remarks: ${remarks}`);
  }

  // Warranty Coverage + General Terms (two-column layout)
  printer.addDashedSeparator();
  printer.addLine();
  addWarrantyAndTermsTwoColumn(printer, warrantyType);

  printer.addDashedSeparator();
  printer.addLine();
  printer.addLine("Signed for Acceptance and Approval: _____________________");
  printer.addLine();
  printer.addLine(`Date: ${new Date().toLocaleDateString()}`);

  return printer;
}
